/**
 * Configuration Management - Task 18
 * Centralized application configuration
 */

interface AppConfig {
  app: {
    name: string;
    version: string;
    // S128: 'test' is what `.env.e2e` sets, and the union used to omit it —
    // so the checked-in E2E build's environment was not in its own type.
    environment: 'development' | 'test' | 'staging' | 'production';
    baseUrl: string;
    apiUrl: string;
  };
  
  firebase: {
    apiKey: string;
    authDomain: string;
    projectId: string;
    storageBucket: string;
    messagingSenderId: string;
    appId: string;
    measurementId?: string;
  };
  
  features: {
    requireEmailVerification: boolean;
    enableAutoSave: boolean;
    enableOfflineMode: boolean;
    enableAnalytics: boolean;
    enableErrorReporting: boolean;
    enablePerformanceMonitoring: boolean;
  };
  
  limits: {
    maxProjectSize: number;
    maxCodeLength: number;
    maxProjectsPerUser: number;
    maxMessagesPerProject: number;
    projectsPerPage: number;
    autoSaveInterval: number;
    sessionTimeout: number;
    inactivityTimeout: number;
  };
  
  api: {
    anthropic: {
      baseUrl: string;
      timeout: number;
      maxRetries: number;
    };
    openai: {
      baseUrl: string;
      timeout: number;
      maxRetries: number;
    };
    gemini: {
      baseUrl: string;
      timeout: number;
      maxRetries: number;
    };
  };
  
  cache: {
    userSettingsTTL: number;
    projectMetadataTTL: number;
    aiModelConfigTTL: number;
    sessionTTL: number;
  };
  
  validation: {
    projectTitleMaxLength: number;
    projectDescriptionMaxLength: number;
    authorNameMaxLength: number;
    emailMaxLength: number;
  };
  
  security: {
    maxLoginAttempts: number;
    loginAttemptWindow: number;
    passwordMinLength: number;
    sessionMaxDuration: number;
    requireStrongPassword: boolean;
    enableCSP: boolean;
  };
}

/**
 * S83: `parseInt` is the wrong parser for a configured limit.
 *
 *   parseInt('unlimited')  -> NaN, and EVERY comparison against NaN is false, so
 *                             `size > NaN` never rejects. One typo in a district's
 *                             `.env` silently removed the project-size and
 *                             message-count ceilings from the build.
 *   parseInt('10MB')       -> 10. The unit suffix a human naturally writes turns
 *                             ten megabytes into ten BYTES: total lockout, equally
 *                             silent, in the opposite direction.
 *   parseInt('1e6')        -> 1. A code-length ceiling of one character.
 *
 * A limit that cannot be parsed falls back to the documented default and says so.
 * Deliberately NOT a throw: a bad `.env` value must not stop the app booting for
 * a classroom, and the default is a value someone chose.
 */
/**
 * The environment name, recognised rather than cast.
 *
 * S128: `env as AppConfig['app']['environment']` made the TYPE say
 * 'development' | 'staging' | 'production' while the VALUE was whatever was in
 * the shell — so a consumer switching on that union had an unreachable-looking
 * default branch that was in fact the live one. And the comparison was `===`, so
 * "Production", "prod" or a trailing space was NOT production: the four
 * hardenings in the production block never ran, and nothing anywhere reported
 * that the name had not been recognised. A district deploy with a capitalised
 * value in `.env.production.local` shipped with email verification not required,
 * no error reporting, no analytics, and CSP off — while `getEnvironment()`
 * cheerfully reported "Production" to any dashboard that printed it.
 */
const ENVIRONMENTS = ['development', 'test', 'staging', 'production'] as const;

const normalizeEnvironment = (
  raw: string | undefined
): AppConfig['app']['environment'] => {
  const trimmed = (raw ?? '').trim().toLowerCase();
  if (!trimmed) return 'development';

  const matched = ENVIRONMENTS.find((name) => name === trimmed);
  if (matched) return matched;

  // 'prod', 'dev' and 'e2e' are the abbreviations the deploy and test scripts
  // actually use. `.env.e2e` sets `test`, which is in the list above.
  if (trimmed === 'prod') return 'production';
  if (trimmed === 'dev') return 'development';
  if (trimmed === 'e2e') return 'test';

  // Anything else is a deployment mistake. Fail LOUD and fail to the safest
  // interpretation available: an unrecognised name is treated as production, so
  // the hardenings run rather than silently not running.
  // eslint-disable-next-line no-console -- config loads before the logger
  console.error(
    `REACT_APP_ENVIRONMENT="${raw}" is not one of ${ENVIRONMENTS.join(', ')}. ` +
      'Treating it as production so the hardenings still apply.'
  );
  return 'production';
};

/**
 * A URL that must be configured in production. S128: the localhost fallback was
 * unconditional, so a live deployment could emit `http://localhost:3000` into a
 * share link or a parental-consent email.
 *
 * Takes the VALUE, never the variable name: Vite's `define` substitutes only a
 * static `process.env.X` member expression, never a computed one — a lookup by
 * variable name would be `undefined` in every build (the trap CLAUDE.md names,
 * and the one S104's feature flags were fixed for). `featureFlags.test.ts`
 * guards that, by scanning for the computed form.
 */
const requiredInProduction = (
  value: string | undefined,
  name: string,
  env: AppConfig['app']['environment'],
  fallback: string
): string => {
  if (value) return value;
  if (env === 'production') {
    // eslint-disable-next-line no-console -- config loads before the logger
    console.error(`${name} is not set. A production build must configure it.`);
    return '';
  }
  return fallback;
};

const numericSetting = (raw: string | undefined, fallback: number, name: string): number => {
  if (raw === undefined || raw.trim() === '') return fallback;
  const value = Number(raw);
  if (!Number.isFinite(value) || value < 0) {
    console.warn(
      `appConfig: ${name} is "${raw}", which is not a usable number. ` +
        `Falling back to ${fallback}.`
    );
    return fallback;
  }
  return value;
};

/** Deep-freeze, so configuration is configuration rather than a writable global. */
const deepFreeze = <T>(value: T): T => {
  if (value === null || typeof value !== 'object' || Object.isFrozen(value)) return value;
  for (const key of Object.keys(value as Record<string, unknown>)) {
    deepFreeze((value as Record<string, unknown>)[key]);
  }
  return Object.freeze(value);
};

class ConfigManager {
  private config: AppConfig;
  
  constructor() {
    this.config = this.loadConfiguration();
  }
  
  /**
   * Load configuration based on environment
   */
  private loadConfiguration(): AppConfig {
    const env = normalizeEnvironment(process.env.REACT_APP_ENVIRONMENT);
    
    const baseConfig: AppConfig = {
      app: {
        // S135: `.env.example` documents REACT_APP_APP_NAME and this was a
        // literal, so an operator preparing a district deployment set a name
        // that nothing read. Trimmed, because a trailing space in a `.env` is
        // invisible and would show up in the UI.
        name: (process.env.REACT_APP_APP_NAME || '').trim() || 'NS Gaming Studio',
        version: process.env.REACT_APP_VERSION || '1.0.0',
        environment: env,
          // S128: the localhost fallback was unconditional, with no "must be set
        // in production" check anywhere — so anything building a link from
        // `baseUrl` (a share URL, a parental-consent email) would emit a
        // localhost address from a live deployment. In production an unset
        // value is a deployment mistake, and the config says so rather than
        // inventing an address.
        baseUrl: requiredInProduction(
          process.env.REACT_APP_BASE_URL,
          'REACT_APP_BASE_URL',
          env,
          'http://localhost:3000'
        ),
        apiUrl: requiredInProduction(
          process.env.REACT_APP_API_URL,
          'REACT_APP_API_URL',
          env,
          'http://localhost:5001'
        )
      },
      
      firebase: {
        apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
        authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
        projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
        storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
        messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || '',
        appId: process.env.REACT_APP_FIREBASE_APP_ID || '',
        measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
      },
      
      features: {
        requireEmailVerification: process.env.REACT_APP_REQUIRE_EMAIL_VERIFICATION === 'true',
        enableAutoSave: process.env.REACT_APP_ENABLE_AUTO_SAVE !== 'false',
        enableOfflineMode: process.env.REACT_APP_ENABLE_OFFLINE_MODE === 'true',
        enableAnalytics: process.env.REACT_APP_ENABLE_ANALYTICS === 'true',
        enableErrorReporting: process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true',
        enablePerformanceMonitoring: process.env.REACT_APP_ENABLE_PERFORMANCE_MONITORING === 'true'
      },
      
      limits: {
        // S135: the default was 10 MB, which is a ceiling Firestore can never
        // honour — a document is capped at 1,048,576 BYTES, so a "10 MB
        // project" is refused by the SDK whatever this says. A configured limit
        // above the hard limit is not a limit. 900 KB leaves room for the title,
        // description, tags and metadata in the same document.
        maxProjectSize: numericSetting(process.env.REACT_APP_MAX_PROJECT_SIZE, 900000, 'REACT_APP_MAX_PROJECT_SIZE'),
        maxCodeLength: numericSetting(process.env.REACT_APP_MAX_CODE_LENGTH, 1000000, 'REACT_APP_MAX_CODE_LENGTH'), // 1MB
        maxProjectsPerUser: numericSetting(process.env.REACT_APP_MAX_PROJECTS_PER_USER, 100, 'REACT_APP_MAX_PROJECTS_PER_USER'),
        maxMessagesPerProject: numericSetting(process.env.REACT_APP_MAX_MESSAGES_PER_PROJECT, 1000, 'REACT_APP_MAX_MESSAGES_PER_PROJECT'),
        projectsPerPage: numericSetting(process.env.REACT_APP_PROJECTS_PER_PAGE, 12, 'REACT_APP_PROJECTS_PER_PAGE'),
        autoSaveInterval: numericSetting(process.env.REACT_APP_AUTO_SAVE_INTERVAL, 30000, 'REACT_APP_AUTO_SAVE_INTERVAL'), // 30 seconds
        sessionTimeout: numericSetting(process.env.REACT_APP_SESSION_TIMEOUT, 28800000, 'REACT_APP_SESSION_TIMEOUT'), // 8 hours
        inactivityTimeout: numericSetting(process.env.REACT_APP_INACTIVITY_TIMEOUT, 1800000, 'REACT_APP_INACTIVITY_TIMEOUT') // 30 minutes
      },
      
      api: {
        anthropic: {
          baseUrl: 'https://api.anthropic.com',
          timeout: 30000,
          maxRetries: 3
        },
        openai: {
          baseUrl: 'https://api.openai.com',
          timeout: 30000,
          maxRetries: 3
        },
        gemini: {
          baseUrl: 'https://generativelanguage.googleapis.com',
          timeout: 30000,
          maxRetries: 3
        }
      },
      
      cache: {
        userSettingsTTL: 600000, // 10 minutes
        projectMetadataTTL: 300000, // 5 minutes
        aiModelConfigTTL: 1800000, // 30 minutes
        sessionTTL: 3600000 // 1 hour
      },
      
      validation: {
        projectTitleMaxLength: numericSetting(process.env.REACT_APP_PROJECT_TITLE_MAX_LENGTH, 100, 'REACT_APP_PROJECT_TITLE_MAX_LENGTH'),
        projectDescriptionMaxLength: numericSetting(process.env.REACT_APP_PROJECT_DESCRIPTION_MAX_LENGTH, 500, 'REACT_APP_PROJECT_DESCRIPTION_MAX_LENGTH'),
        authorNameMaxLength: numericSetting(process.env.REACT_APP_AUTHOR_NAME_MAX_LENGTH, 100, 'REACT_APP_AUTHOR_NAME_MAX_LENGTH'),
        emailMaxLength: numericSetting(process.env.REACT_APP_EMAIL_MAX_LENGTH, 254, 'REACT_APP_EMAIL_MAX_LENGTH')
      },
      
      security: {
        maxLoginAttempts: 5,
        loginAttemptWindow: 900000, // 15 minutes
        passwordMinLength: 8,
        sessionMaxDuration: 28800000, // 8 hours
        requireStrongPassword: true,
        enableCSP: env === 'production'
      }
    };
    
    // S128: `requireEmailVerification` defaulted PERMISSIVE outside production —
    // unset meant "do not require it". Everything else in `features` is an
    // opt-in convenience, so the restrictive default and the permissive default
    // are the same shape of code (`=== 'true'`) and only this one was wrong: any
    // non-production build — staging, a pilot, an e2e build pointed at a real
    // project — reported that unverified email was fine. It is opt-OUT now.
    baseConfig.features.requireEmailVerification =
      process.env.REACT_APP_REQUIRE_EMAIL_VERIFICATION !== 'false';

    // S136: `enableErrorReporting` had the same permissive default, and the
    // consequence was the same shape: an error in a staging pilot was neither
    // forwarded nor logged, because `monitoring.logError` required BOTH
    // production and the flag. Diagnostics are not the privacy-sensitive kind of
    // telemetry — every event is scrubbed in `sentry.ts` before it leaves, and
    // with no DSN provisioned the forward is a no-op — so this is opt-OUT
    // outside development, while analytics and performance monitoring stay
    // opt-IN. Development still keeps errors in the console, where they belong.
    baseConfig.features.enableErrorReporting =
      env === 'development'
        ? process.env.REACT_APP_ENABLE_ERROR_REPORTING === 'true'
        : process.env.REACT_APP_ENABLE_ERROR_REPORTING !== 'false';

    // Override with environment-specific settings
    if (env === 'production') {
      baseConfig.features.requireEmailVerification = true;
      baseConfig.features.enableAnalytics = true;
      baseConfig.features.enableErrorReporting = true;
      baseConfig.security.enableCSP = true;
    }
    
    // S83: there was no clone and no freeze anywhere in this file, and `get()`
    // handed out the live object. One line in any component — or in code a bug
    // lets run — permanently changed the configuration every later reader sees,
    // for the life of the tab. `requireStrongPassword`, `maxLoginAttempts` and
    // `passwordMinLength` were presented as configuration and were writable
    // globals. Configuration is read-only.
    return deepFreeze(baseConfig);
  }
  
  /**
   * Get configuration value
   */
  get<K extends keyof AppConfig>(key: K): AppConfig[K] {
    return this.redactForEnvironment(this.config[key]);
  }
  
  /**
   * Get nested configuration value
   */
  getValue(path: string): unknown {
    // S83: `firebase.apiKey` was reachable here in production while `getAll()`
    // redacted it, which made the redaction cosmetic. Nothing in `src/` reads the
    // key through this class — `config/firebase.ts` reads the environment
    // directly — so hiding it here costs nothing and removes the path by which a
    // debug helper copies it into a log.
    if (!this.isDevelopment() && path === 'firebase.apiKey') {
      return '[HIDDEN]';
    }

    const keys = path.split('.');
    let value: unknown = this.config;

    for (const key of keys) {
      if (value === null || typeof value !== 'object') {
        return undefined;
      }
      value = (value as Record<string, unknown>)[key];
      if (value === undefined) {
        return undefined;
      }
    }

    return value;
  }
  
  /**
   * Check if feature is enabled
   */
  isFeatureEnabled(feature: keyof AppConfig['features']): boolean {
    return this.config.features[feature];
  }
  
  /**
   * Get environment
   */
  getEnvironment(): string {
    return this.config.app.environment;
  }
  
  /**
   * Is production environment
   */
  isProduction(): boolean {
    return this.config.app.environment === 'production';
  }
  
  /**
   * Is development environment
   */
  isDevelopment(): boolean {
    return this.config.app.environment === 'development';
  }
  
  /**
   * Get API configuration
   */
  getApiConfig(provider: 'anthropic' | 'openai' | 'gemini') {
    return this.config.api[provider];
  }
  
  /**
   * Get all configuration (for debugging)
   */
  getAll(): AppConfig {
    if (this.isDevelopment()) {
      return { ...this.config };
    }
    // S83: the redaction here was cosmetic. `get('firebase').apiKey` and
    // `getValue('firebase.apiKey')` are the DOCUMENTED accessors and neither
    // redacted, so anything logging configuration through the ordinary path
    // printed the key in production. Redaction now happens in one place, applied
    // by every accessor — see `redactForEnvironment` below.
    //
    // Note this is defence in depth, not a secret: a Firebase Web API key is
    // public by design and is in the shipped bundle. What it stops is the key
    // being copied into a log aggregator or a support ticket by a debug helper.
    return this.redactForEnvironment(this.config);
  }

  /**
   * S83: hide the values that should not travel out of a production build in a
   * log line. Returns the live object untouched in development, where seeing the
   * real configuration is the point.
   */
  private redactForEnvironment<T>(value: T): T {
    if (this.isDevelopment()) return value;
    if (value === this.config) {
      return {
        ...this.config,
        firebase: { ...this.config.firebase, apiKey: '[HIDDEN]' },
      } as unknown as T;
    }
    if (value === (this.config.firebase as unknown)) {
      return { ...this.config.firebase, apiKey: '[HIDDEN]' } as unknown as T;
    }
    return value;
  }
}

// Export singleton instance
export const appConfig = new ConfigManager();

// Export types
export type { AppConfig, ConfigManager };